import { useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthActions } from '@/hooks/use-auth';
import { useTheme } from '@/context/ThemeContext';
import { Eye, EyeSlash, EnvelopeSimple, Lock, User, Buildings, ArrowLeft, CircleNotch } from '@phosphor-icons/react';

type AuthMode = 'login' | 'register' | 'forgot-password' | 'reset-password';

interface LoginPageProps {
  initialMode?: AuthMode;
}

export function LoginPage({ initialMode = 'login' }: LoginPageProps) {
  const { loginAndRedirect, registerAndRedirect, forgotPassword, resetPassword, isDemoMode } = useAuthActions();
  const { accent } = useTheme();
  const location = useLocation();

  const [mode, setMode] = useState<AuthMode>(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') ? 'reset-password' : initialMode;
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [firmName, setFirmName] = useState('');
  const [resetToken] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') ?? '';
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const clearForm = () => {
    setError('');
    setSuccess('');
    setPassword('');
    setConfirmPassword('');
  };

  const switchMode = (newMode: AuthMode) => {
    clearForm();
    setMode(newMode);
  };

  const validateEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const validatePassword = (value: string): boolean => {
    return value.length >= 8;
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setError('Password is required.');
      return;
    }

    setLoading(true);
    try {
      await loginAndRedirect(email, password);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid credentials. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!validatePassword(password)) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!firmName.trim()) {
      setError('Firm name is required.');
      return;
    }

    setLoading(true);
    try {
      await registerAndRedirect(name, email, password, firmName);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      await forgotPassword(email);
      setSuccess('If an account with that email exists, a password reset link has been sent.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send reset email. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validatePassword(password)) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(resetToken, password);
      setSuccess('Password reset successfully. You can now sign in.');
      setTimeout(() => switchMode('login'), 2000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to reset password. The link may have expired.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const accentBg = accent.tailwind;
  const accentText = accent.tailwindText;

  const inputClasses =
    'w-full rounded-lg border border-gray-700 bg-[#1A1D25] pl-10 pr-4 py-2.5 text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 transition-colors';

  const renderIcon = (Icon: typeof EnvelopeSimple) => (
    <Icon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0D0F14] p-4">
      <div className="w-full max-w-md">
        {/* Logo / Branding */}
        <div className="mb-8 text-center">
          <div
            className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl ${accentBg} text-white text-xl font-bold shadow-lg`}
          >
            B&M
          </div>
          <h1 className="text-2xl font-bold text-white">BAS &amp; More</h1>
          <p className="mt-1 text-sm text-gray-300">Australian Bookkeeping Automation Platform</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-gray-800 bg-[#13151B] p-8 shadow-2xl">
          {/* Mode title */}
          <div className="mb-6">
            {(mode === 'forgot-password' || mode === 'reset-password') && (
              <button
                type="button"
                onClick={() => switchMode('login')}
                className={`mb-3 flex items-center gap-1 text-sm ${accentText} hover:underline`}
              >
                <ArrowLeft size={14} />
                Back to sign in
              </button>
            )}
            <h2 className="text-xl font-semibold text-white">
              {mode === 'login' && 'Sign in to your account'}
              {mode === 'register' && 'Create your account'}
              {mode === 'forgot-password' && 'Reset your password'}
              {mode === 'reset-password' && 'Set new password'}
            </h2>
            <p className="mt-1 text-sm text-gray-300">
              {mode === 'login' && 'Enter your credentials to continue'}
              {mode === 'register' && 'Get started with BAS & More'}
              {mode === 'forgot-password' && "Enter your email and we'll send a reset link"}
              {mode === 'reset-password' && 'Choose a strong password for your account'}
            </p>
          </div>

          {/* Error / Success messages */}
          {error && (
            <div role="alert" className="mb-4 rounded-lg border border-red-800 bg-red-900/30 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}
          {success && (
            <div role="status" className="mb-4 rounded-lg border border-emerald-800 bg-emerald-900/30 px-4 py-3 text-sm text-emerald-300">
              {success}
            </div>
          )}

          {/* Demo mode banner */}
          {isDemoMode && (
            <div className="mb-4 rounded-lg border border-amber-800 bg-amber-900/30 px-4 py-3 text-sm text-amber-300">
              Demo mode is active. Authentication is bypassed.
            </div>
          )}

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} aria-busy={loading} className="space-y-4">
              <div className="relative">
                <label htmlFor="login-email" className="sr-only">
                  Email
                </label>
                {renderIcon(EnvelopeSimple)}
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className={inputClasses}
                  placeholder="Email address"
                  autoComplete="email"
                />
              </div>

              <div className="relative">
                <label htmlFor="login-password" className="sr-only">
                  Password
                </label>
                {renderIcon(Lock)}
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`${inputClasses} pr-10`}
                  placeholder="Password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => switchMode('forgot-password')}
                  className={`text-sm ${accentText} hover:underline`}
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full rounded-lg ${accentBg} px-4 py-2.5 font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center gap-2`}
              >
                {loading && <CircleNotch size={18} className="animate-spin" />}
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}

          {/* Register Form */}
          {mode === 'register' && (
            <form onSubmit={handleRegister} aria-busy={loading} className="space-y-4">
              <div className="relative">
                <label htmlFor="register-name" className="sr-only">
                  Full name
                </label>
                {renderIcon(User)}
                <input
                  id="register-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                  className={inputClasses}
                  placeholder="Full name"
                  autoComplete="name"
                />
              </div>

              <div className="relative">
                <label htmlFor="register-email" className="sr-only">
                  Email
                </label>
                {renderIcon(EnvelopeSimple)}
                <input
                  id="register-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={inputClasses}
                  placeholder="Email address"
                  autoComplete="email"
                />
              </div>

              <div className="relative">
                <label htmlFor="register-firm" className="sr-only">
                  Firm name
                </label>
                {renderIcon(Buildings)}
                <input
                  id="register-firm"
                  type="text"
                  value={firmName}
                  onChange={(e) => setFirmName(e.target.value)}
                  required
                  className={inputClasses}
                  placeholder="Firm / Business name"
                />
              </div>

              <div className="relative">
                <label htmlFor="register-password" className="sr-only">
                  Password
                </label>
                {renderIcon(Lock)}
                <input
                  id="register-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`${inputClasses} pr-10`}
                  placeholder="Password (min 8 characters)"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="relative">
                <label htmlFor="register-confirm" className="sr-only">
                  Confirm password
                </label>
                {renderIcon(Lock)}
                <input
                  id="register-confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className={inputClasses}
                  placeholder="Confirm password"
                  autoComplete="new-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full rounded-lg ${accentBg} px-4 py-2.5 font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center gap-2`}
              >
                {loading && <CircleNotch size={18} className="animate-spin" />}
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot-password' && (
            <form onSubmit={handleForgotPassword} aria-busy={loading} className="space-y-4">
              <div className="relative">
                <label htmlFor="forgot-email" className="sr-only">
                  Email
                </label>
                {renderIcon(EnvelopeSimple)}
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className={inputClasses}
                  placeholder="Email address"
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full rounded-lg ${accentBg} px-4 py-2.5 font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center gap-2`}
              >
                {loading && <CircleNotch size={18} className="animate-spin" />}
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          {/* Reset Password Form */}
          {mode === 'reset-password' && (
            <form onSubmit={handleResetPassword} aria-busy={loading} className="space-y-4">
              <div className="relative">
                <label htmlFor="reset-password" className="sr-only">
                  New password
                </label>
                {renderIcon(Lock)}
                <input
                  id="reset-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  className={`${inputClasses} pr-10`}
                  placeholder="New password (min 8 characters)"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="relative">
                <label htmlFor="reset-confirm" className="sr-only">
                  Confirm new password
                </label>
                {renderIcon(Lock)}
                <input
                  id="reset-confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className={inputClasses}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full rounded-lg ${accentBg} px-4 py-2.5 font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center gap-2`}
              >
                {loading && <CircleNotch size={18} className="animate-spin" />}
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          {/* Mode switcher links */}
          <div className="mt-6 text-center text-sm text-gray-500">
            {mode === 'login' && (
              <p>
                Don&apos;t have an account?{' '}
                <button type="button" onClick={() => switchMode('register')} className={`${accentText} hover:underline`}>
                  Create account
                </button>
              </p>
            )}
            {mode === 'register' && (
              <p>
                Already have an account?{' '}
                <button type="button" onClick={() => switchMode('login')} className={`${accentText} hover:underline`}>
                  Sign in
                </button>
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-gray-600">
          &copy; {new Date().getFullYear()} BAS &amp; More. All rights reserved.
        </p>
      </div>
    </div>
  );
}
